/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Shared type definitions for user stories and epics
 * Used across the platform for requirements tracking and project planning
 */

// User Story Status
export type UserStoryStatus = 'Not Started' | 'In Progress' | 'Ready For Review' | 'In Review' | 'Completed' | 'Blocked' | 'On Hold';

export const USER_STORY_STATUSES: UserStoryStatus[] = [
  'Not Started',
  'In Progress',
  'Ready For Review',
  'In Review',
  'Completed',
  'Blocked',
  'On Hold'
];

// Acceptance Criterion Status
export type AcceptanceCriterionStatus = 'Required' | 'Scoped' | 'Ready For Review' | 'Accepted';

export const ACCEPTANCE_CRITERION_STATUSES: AcceptanceCriterionStatus[] = [
  'Required',
  'Scoped',
  'Ready For Review',
  'Accepted'
];

export const DEFAULT_ACCEPTANCE_CRITERION_STATUS: AcceptanceCriterionStatus = 'Required';

// Special Release Values for acceptance criteria
export const SPECIAL_RELEASE_VALUES = ['Unscheduled', 'Deferred'] as const;
export type SpecialReleaseValue = typeof SPECIAL_RELEASE_VALUES[number];
export const DEFAULT_RELEASE_VALUE: SpecialReleaseValue = 'Unscheduled';

/**
 * Acceptance Criterion - a single acceptance criterion with optional release and status metadata.
 *
 * This is the canonical shape everywhere acceptance criteria are handled inside the
 * application. Anything that reads or writes criteria (rendering, persistence, tool
 * schemas) should use `AcceptanceCriterion`; never use a bare `string`.
 *
 * The only place a raw `string` is valid is at the *boundary* where external input is
 * parsed (e.g. markdown bullets, JIRA descriptions, MCP tool args). That input is
 * immediately upgraded to `AcceptanceCriterion` via {@link normalizeAcceptanceCriteria}.
 */
export interface AcceptanceCriterion {
  text: string;
  release?: string; // Release ID (e.g., "SPRINT-01", "REL-001")
  status?: AcceptanceCriterionStatus; // Status (defaults to "Required")
}

/**
 * Raw shape accepted at external boundaries (CSV, JIRA API, Linear API, MCP tool args,
 * legacy markdown). Internal code should not use this type directly - use
 * {@link AcceptanceCriterion} and call {@link normalizeAcceptanceCriteria} once, at
 * the boundary, to upgrade strings into the canonical object shape.
 */
export type AcceptanceCriterionInput = string | AcceptanceCriterion;

/**
 * Upgrade a possibly-mixed list of acceptance criteria to the canonical
 * `AcceptanceCriterion[]` shape. This should only be called at the boundary between
 * external input (parsed markdown, CSV rows, JIRA/Linear payloads, MCP tool args) and
 * the internal data model - internal code should already have `AcceptanceCriterion[]`.
 *
 * Explicitly constructs each object so properties survive JSON serialization.
 */
export function normalizeAcceptanceCriteria(
  criteria: AcceptanceCriterionInput[] | undefined
): AcceptanceCriterion[] {
  if (!criteria) return [];
  return criteria.map(c => {
    if (typeof c === 'string') {
      return { text: c, status: DEFAULT_ACCEPTANCE_CRITERION_STATUS };
    }
    return {
      text: c.text,
      release: c.release,
      status: c.status || DEFAULT_ACCEPTANCE_CRITERION_STATUS,
    };
  });
}

/**
 * Render a single acceptance criterion as plain text (e.g. `"text [REL-001]"`).
 *
 * Use this anywhere an `AcceptanceCriterion` needs to be embedded in a string (markdown,
 * CSV, PDF, JIRA/Linear description bodies, log messages). Using this helper instead of
 * string-interpolating the object directly prevents the classic `[object Object]` bug
 * that used to appear when callers treated criteria as strings.
 */
export function formatAcceptanceCriterion(criterion: AcceptanceCriterion): string {
  return criterion.release ? `${criterion.text} [${criterion.release}]` : criterion.text;
}

/**
 * Render a list of acceptance criteria as bullet lines. Returns an empty string when
 * the list is empty so callers can concatenate unconditionally.
 *
 * @param criteria - The criteria to render.
 * @param bullet   - Bullet marker to use (defaults to `"-"`).
 * @param indent   - Leading indent applied to every bullet line (defaults to `""`).
 */
export function formatAcceptanceCriteriaList(
  criteria: AcceptanceCriterion[],
  bullet: string = '-',
  indent: string = ''
): string {
  if (!criteria.length) return '';
  return criteria
    .map(c => `${indent}${bullet} ${formatAcceptanceCriterion(c)}`)
    .join('\n');
}

/**
 * User Story - represents a single user story in the agile format
 */
export interface UserStory {
  id: string;
  title: string;
  role: string; // "As an Admin"
  goal: string; // "I want to..."
  benefit: string; // "So that..."
  acceptanceCriteria: AcceptanceCriterion[];
  section?: string; // The section in the document (e.g., "Admin User Stories")
  epicId?: string; // Epic this story belongs to
  status?: UserStoryStatus; // Status field
  links?: string[]; // Links to issues, documents, or other resources (e.g., ["BUG-001", "TASK-042", "docs/design.md", "https://..."])
  product?: string; // Product this story belongs to (e.g., "PROD-001")
  filePath?: string; // Path to the file containing this story
}

/**
 * Epic - represents a collection of related user stories
 */
export interface Epic {
  id: string;
  title: string;
  description: string;
  userStoryIds: string[]; // User stories that belong to this epic
  section?: string; // The section in the document
  status?: UserStoryStatus; // Status field
  product?: string; // Product this epic belongs to (e.g., "PROD-001")
  filePath?: string; // Path to the file containing this epic
}

/**
 * Result of parsing user stories from markdown
 */
export interface ParsedUserStories {
  stories: UserStory[];
  epics: Epic[];
  errors: string[];
  warnings: string[];
}

